import { useState, useCallback } from 'react';
import type { AuctionBid } from '../../types/problems';
import { getVulnerabilityMap } from '../../utils/vulnerabilityMapper';
import { combineBidAnnotation } from '../../utils/bidAnnotation';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { BidCell } from './BidCell';

interface AuctionTableProps {
  auction: AuctionBid[];
  vulnerability: string;
  decisionSeat: 'N' | 'E' | 'S' | 'W';
  /** When true, display 'P' instead of 'Pass' to keep the table narrow. */
  abbreviatePass?: boolean;
  /** When true, use auto-width table layout (no table-fixed w-full) for compact display. */
  compact?: boolean;
  /**
   * Seat whose call is being asked for. Renders a '?' in that seat's column after the last
   * revealed bid, so the learner can see WHERE in the auction the question sits. Needed even
   * when the auction is empty (a Yes/No opening question reveals no calls at all).
   */
  pendingSeat?: Seat | null;
  /**
   * Background utility for the sticky header row. It must be opaque or rows scroll through
   * the seat letters, so it cannot simply be transparent — hosts on a non-white panel pass
   * their own panel colour instead (the felt table's cream, for one).
   */
  headerBgClass?: string;
  /**
   * Optional replacement for the built-in generic-placeholder annotation (feature 029). Defaults
   * to {@link getGenericBidComment}, so omitting it produces exactly the annotations this table
   * has always produced.
   *
   * It exists because the default reads `M` case-insensitively and therefore calls the
   * lowercase-`m` MINOR placeholders "Major" — harmless on the bidding pages, which hold almost
   * none, and wrong on the conceptual set, which holds twenty. Rather than change a shared rule
   * under the pages that depend on it, the caller that knows better supplies its own.
   */
  bidCommentResolver?: (call: string) => string | null;
  /**
   * Optional per-call renderer, threaded to {@link BidCell}. Return `null` for a call the caller
   * does not handle and the shared default renders it (feature 029 — the either/or calls such as
   * `4c/4d`, which appear in no other data set).
   */
  callFormatter?: (call: string) => JSX.Element | null;
}

const SEATS = ['W', 'N', 'E', 'S'] as const;
type Seat = typeof SEATS[number];

/** Placeholder call marking the seat still to act. Never a real bid, so never normalized. */
const PENDING_CALL = '?';

/** Normalize bid notation per FR-033: p/P → Pass, nt → NT (case-insensitive) */
function normalizeBid(call: string): string {
  if (call.toLowerCase() === 'p') return 'Pass';
  return call.replace(/nt$/i, 'NT');
}

/**
 * Detect generic suit placeholders and return an auto-generated comment.
 * Returns null for concrete bids. Works for any level (1M, 2M, 3x, etc.).
 *
 * Exported (feature 029) so a caller supplying its own `bidCommentResolver` can handle the shapes
 * it knows better and delegate the rest here, rather than restating these rules and drifting.
 * Note the `/i` flags: `2m` matches the MAJOR rule. That is wrong for data that uses lowercase
 * `m` for a minor, and is why the resolver prop exists — the behaviour is left as it is because
 * the bidding pages have depended on it since feature 005.
 */
export function getGenericBidComment(call: string): string | null {
  if (/^\d[HS]\/\d[HS]$/i.test(call)) return 'Major (H/S)';
  // The generic suit letters, with the meanings the client confirmed on 2026-09-11 (feature 029,
  // FR-017e). Case-SENSITIVE on M/m — the case is the meaning. Until then `m` was read as a
  // major and `x` as a minor, and `y` was not read at all.
  if (/^\dM$/.test(call)) return 'Major (H/S)';
  if (/^\dm$/.test(call)) return 'Minor (C/D)';
  if (/^\d[xX]$/.test(call)) return 'Any suit';
  if (/^\d[yY]$/.test(call)) return 'Any suit above x';
  return null;
}

/**
 * Build auction rows from bids.
 *
 * @param auction  - ordered bid list
 * @param rotate   - when true, rotate header so dealer is column 0 (no dash padding)
 * @returns { header: Seat[], rows: (string | null)[][] }
 */
export function buildAuctionRows(
  auction: AuctionBid[],
  rotate: boolean
): { header: readonly Seat[]; rows: (string | null)[][] } {
  const firstBid = auction[0];
  const dealerIndex =
    auction.length > 0 && firstBid
      ? SEATS.indexOf(firstBid.seat as Seat)
      : 0;

  // Header order: rotated on phone, fixed on desktop
  const header: readonly Seat[] = rotate
    ? ([...SEATS.slice(dealerIndex), ...SEATS.slice(0, dealerIndex)] as const)
    : SEATS;

  const rows: (string | null)[][] = [];
  let currentRow: (string | null)[] = [null, null, null, null];

  if (!rotate) {
    // Desktop/tablet: fill dash padding before dealer
    for (let i = 0; i < dealerIndex; i++) {
      currentRow[i] = '-';
    }
  }

  // Starting column: 0 when rotated (dealer is already at index 0), dealerIndex otherwise
  let seatIndex = rotate ? 0 : dealerIndex;

  for (const bid of auction) {
    currentRow[seatIndex] = normalizeBid(bid.call);
    seatIndex++;
    if (seatIndex >= 4) {
      rows.push(currentRow);
      currentRow = [null, null, null, null];
      seatIndex = 0;
    }
  }

  // Add the last row if it has any content
  if (currentRow.some((cell) => cell !== null)) {
    rows.push(currentRow);
  }

  return { header, rows };
}

/**
 * Validate that an auction sequence is bridge-legal.
 *
 * Rule: once the bidding has been opened (at least one non-pass bid),
 * three consecutive passes end the auction. Any bid appearing after
 * three consecutive passes indicates corrupted data (e.g. gap-filled
 * missing bids replaced with Pass).
 *
 * A passed-out hand (four initial passes, no opening bid) is valid.
 *
 * @returns `true` if the auction is valid, `false` if it contains
 *          a bid after three consecutive passes following an opening.
 */
export function validateAuctionIntegrity(auction: AuctionBid[]): boolean {
  if (auction.length === 0) return true;

  let hasOpened = false;
  let consecutivePasses = 0;

  for (const bid of auction) {
    const isPass = bid.call.toLowerCase() === 'p' || bid.call.toLowerCase() === 'pass';

    if (!hasOpened) {
      if (isPass) {
        consecutivePasses++;
        // Four initial passes = passed-out hand (valid, auction ends)
        if (consecutivePasses >= 4) return true;
      } else {
        hasOpened = true;
        consecutivePasses = 0;
      }
    } else {
      if (isPass) {
        consecutivePasses++;
        if (consecutivePasses >= 3) {
          // Auction should end here — any further bids are invalid
          // Check if there are more bids after this one
          const currentIndex = auction.indexOf(bid);
          if (currentIndex < auction.length - 1) return false;
        }
      } else {
        consecutivePasses = 0;
      }
    }
  }

  return true;
}

export function AuctionTable({
  auction,
  vulnerability,
  decisionSeat,
  abbreviatePass,
  compact,
  pendingSeat,
  headerBgClass = 'bg-card',
  bidCommentResolver = getGenericBidComment,
  callFormatter,
}: AuctionTableProps) {
  const vulnMap = getVulnerabilityMap(vulnerability, decisionSeat);

  // The '?' is appended as an ordinary call so the existing row-building and rotation
  // handle it: bidding runs strictly in rotation, so the seat still to act is always the
  // next column after the last revealed bid. When nothing has been revealed yet, that seat
  // is by definition the dealer, which is exactly what buildAuctionRows anchors column 0 to.
  const withPending: AuctionBid[] = pendingSeat
    ? [...auction, { seat: pendingSeat, call: PENDING_CALL }]
    : auction;

  // T319 (v2.2): Single popup state - only one popup open at a time
  const [openPopupId, setOpenPopupId] = useState<string | null>(null);

  const handleOpenPopup = useCallback((popupId: string) => {
    setOpenPopupId(popupId); // Opening new popup closes any previous
  }, []);

  const handleClosePopup = useCallback(() => {
    setOpenPopupId(null);
  }, []);

  // FR-013: Esc closes the open alert popup via the shared hook (constitution
  // Principle VI "Modal Esc dismissal"). Re-tap / open-another / pointer-leave
  // dismissal stays in BidCell; this adds keyboard dismissal for the one open popup.
  useEscapeClose(handleClosePopup, openPopupId !== null);

  // Always rotate header so dealer's direction is column 0 (all viewports)
  const { header, rows: rawRows } = buildAuctionRows(withPending, true);

  // When abbreviatePass is set, shorten 'Pass' to 'P' for a compact display
  const rows = abbreviatePass
    ? rawRows.map(row => row.map(cell => (cell === 'Pass' ? 'P' : cell)))
    : rawRows;

  // Build annotation lookup: map cell position to the combined bid annotation.
  // The comment half is the bid's own comment or an auto-injected generic suit
  // placeholder (M, x, slash notation); the alert half is the per-bid BBO alert
  // (feature 014). combineBidAnnotation puts the alert first on its own line and
  // returns null when neither is present (so the cell stays unhighlighted).
  const commentLookup = new Map<string, string>();
  // Which cell holds the pending marker, by POSITION. Matching on the "?" text instead
  // would also catch the overcalling data-editing display, which legitimately renders a
  // masked bid as "?" and must keep its own styling.
  let pendingCellKey: string | null = null;
  let bidIdx = 0;
  for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
    for (let cellIdx = 0; cellIdx < 4; cellIdx++) {
      const cell = rows[rowIdx]?.[cellIdx];
      if (cell !== null && cell !== '-') {
        if (pendingSeat && bidIdx === withPending.length - 1) {
          pendingCellKey = `${rowIdx}-${cellIdx}`;
        }
        const auctionBid = withPending[bidIdx];
        const comment = auctionBid?.comment || bidCommentResolver(auctionBid?.call ?? '');
        const annotation = combineBidAnnotation(auctionBid?.alert, comment);
        if (annotation) {
          commentLookup.set(`${rowIdx}-${cellIdx}`, annotation);
        }
        bidIdx++;
      }
    }
  }

  const getSeatStyle = (seat: Seat) => {
    const isVulnerable = vulnMap[seat];
    return isVulnerable ? 'text-red-600 font-bold' : 'text-ink font-semibold';
  };

  // T265: formatCall uses BidCell for popup support (v2.2 FR-134, FR-135)
  // FR-076: Pass column position for tooltip alignment to prevent edge clipping
  const formatCall = (call: string | null, rowIndex: number, cellIndex: number) => {
    if (call === null) return '';

    // The pending marker is not a bid: it gets no comment popup, and it is labelled for
    // screen readers because a bare "?" announces as nothing useful.
    if (pendingCellKey === `${rowIndex}-${cellIndex}`) {
      return (
        <span className="font-bold text-primary-700" data-testid="auction-pending">
          <span aria-hidden="true">?</span>
          <span className="sr-only">Your call</span>
        </span>
      );
    }

    // Create unique ID for popup state management
    const popupId = `${rowIndex}-${cellIndex}`;

    // Determine column position for tooltip alignment
    // cellIndex 0 = left edge, cellIndex 3 = right edge
    const columnPosition: 'left' | 'center' | 'right' =
      cellIndex === 0 ? 'left' : cellIndex === 3 ? 'right' : 'center';

    // Use BidCell for all bids to support comment popups
    // CSS handles compact mode via short: variant
    const cellComment = commentLookup.get(`${rowIndex}-${cellIndex}`) ?? null;
    return (
      <BidCell
        bidText={call}
        comment={cellComment}
        isPopupOpen={openPopupId === popupId}
        onOpenPopup={() => handleOpenPopup(popupId)}
        onClosePopup={handleClosePopup}
        columnPosition={columnPosition}
        callFormatter={callFormatter}
      />
    );
  };

  return (
    // Use table-fixed with w-full to ensure columns stretch evenly to fill container width
    // CSS media query: short: variant applies at viewport height < 750px
    // Normal: seatHeader 1.25rem, text 1.5rem (from problemStyles.ts AUCTION_TABLE_STYLES)
    // Short: seatHeader 0.875rem, text 1rem
    <table className={`border-collapse text-base ${compact ? 'w-auto' : 'w-full table-fixed'}`}>
      {/* T323 (v2.2 FR-138): Sticky header for direction labels */}
      <thead className={`sticky top-0 z-10 ${headerBgClass}`}>
        <tr>
          {header.map((seat) => (
            <th
              key={seat}
              className={`text-center py-1 ${compact ? 'px-2.5' : 'px-3'} short:py-0.5 short:px-2 border-b-2 border-line-strong ${compact ? '' : 'w-1/4'} short:text-sm ${getSeatStyle(seat)}`}
              style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)' }}
            >
              {seat}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td
                key={cellIndex}
                className={`text-center py-0.5 ${compact ? 'px-2.5' : 'px-3'} short:py-0.5 short:px-2 border-b border-line font-medium text-ink whitespace-nowrap ${compact ? '' : 'w-1/4'}`}
              >
                {formatCall(cell, rowIndex, cellIndex)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
